// react
import { useSelector } from 'react-redux';

// tanstack
import { Navigate } from '@tanstack/react-router';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

/**
 * ServiceGuard component - Conditionally renders children based on whether the
 * selected account has access to a specific service (or the user is an admin).
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render when access is granted
 * @param {string|string[]} props.serviceNames - The service name(s) to check (e.g. 'WeekScheduler' or ['WeekScheduler', 'Alarms'])
 * @param {React.ReactNode} [props.fallback=null] - Content to render when access is denied
 * @param {boolean} [props.adminBypass=true] - Whether admins bypass the service check
 *
 * @example
 * // Hide a button unless the account has the WeekScheduler service
 * <ServiceGuard serviceNames="WeekScheduler">
 *   <Button>Open Week Schedule</Button>
 * </ServiceGuard>
 *
 * @example
 * // Hide a button unless the account has at least one of the listed services
 * <ServiceGuard serviceNames={['WeekScheduler', 'Alarms']}>
 *   <Button>Open Panel</Button>
 * </ServiceGuard>
 *
 * @example
 * // Show a fallback when access is denied
 * <ServiceGuard serviceNames="WeekScheduler" fallback={<p>Upgrade required</p>}>
 *   <WeekSchedulerPanel />
 * </ServiceGuard>
 *
 * @example
 * // Disable admin bypass so even admins must have the service
 * <ServiceGuard serviceNames="WeekScheduler" adminBypass={false}>
 *   <WeekSchedulerPanel />
 * </ServiceGuard>
 */
export const ServiceGuard = ({
  children,
  serviceNames,
  fallback = <Navigate to="/$accountId/missing-service" />,
  adminBypass = true,
}) => {
  const { isAdmin } = useSessionAuth();
  const selectedAccount = useSelector(getSelectedAccount);

  if (!selectedAccount?.AccountId) return null;

  const names = Array.isArray(serviceNames) ? serviceNames : [serviceNames];
  const hasService = selectedAccount?.Services?.some((s) =>
    names.includes(s.Name),
  );

  const hasAccess = hasService || (adminBypass && isAdmin);

  return hasAccess ? children : fallback;
};
